import { useQuery } from "@apollo/client";
import {
  CompanyContainer,
  CompanyContentContainer,
  CompanyTabsContent,
  CompanyTabsList,
} from "components/company/v3/CompanyContainers";
import BrazilianCalendarContent from "components/company/v3/brazilian/contents/events/BrazilianCalendarContent";
import BrazilianDividendsContent from "components/company/v3/brazilian/contents/events/BrazilianDividendsContent";
import BrazilianIpesContent from "components/company/v3/brazilian/contents/events/BrazilianIpesContent";
import BrazilianRightsIssuesContent from "components/company/v3/brazilian/contents/events/BrazilianRightsIssuesContent";
import BrazilianStockDividendsContent from "components/company/v3/brazilian/contents/events/BrazilianStockDividendsContent";
import BrazilianStockSplitContent from "components/company/v3/brazilian/contents/events/BrazilianStockSplitContent";
import { CompanyEventsTabEnum } from "components/company/v3/menu/enums";
import type { EventsCalendarState } from "components/investorrelations/EventsCalendarLocalState";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import classNames from "components/ui/classNames";
import { Tabs } from "components/ui/Tabs";
import useSelfie from "lib/hooks/useSelfie";
import BrazilianIssuerDividendsQuery from "lib/queries/BrazilianIssuerDividendsQuery";
import GlobalCorporateActionsQuery from "lib/queries/GlobalCorporateActionsQuery";
import type {
  IBrazilianIssuerDividendsQuery,
  IBrazilianIssuerDividendsQueryVariables,
} from "lib/queries/__generated__/BrazilianIssuerDividendsQuery.generated";
import type {
  IGlobalCorporateActionsQuery,
  IGlobalCorporateActionsQueryVariables,
} from "lib/queries/__generated__/GlobalCorporateActionsQuery.generated";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import {
  CalendarDaysIcon,
  CirclePercentIcon,
  HandCoinsIcon,
  MegaphoneIcon,
  SquareSplitHorizontalIcon,
  TicketPercentIcon,
} from "lucide-react";
import { useState } from "react";
import { useIntl } from "react-intl";
import { ISecurityCountry } from "types/graphqlTypes";

interface BrazilianEventsBoxProps extends EventsCalendarState {
  tickerSymbol: string;
  isRenderPageDefault?: boolean;
  tab: CompanyEventsTabEnum;
  onChangeTab: (tab: CompanyEventsTabEnum) => void;
}

export default function BrazilianEventsBox({
  tickerSymbol,
  isRenderPageDefault = false,
  tab,
  onChangeTab,
  ...filterState
}: BrazilianEventsBoxProps) {
  const [filterDividendsByYear, setFilterDividendsByYear] = useState<number | null>(null);
  const [enabledFullHeight, setEnabledFullHeight] = useState(false);
  const viewer = useSelfie();
  const intl = useIntl();

  const { data: globalCorporateActionsData, loading: globalCorporateActionsLoading } = useQuery<
    IGlobalCorporateActionsQuery,
    IGlobalCorporateActionsQueryVariables
  >(GlobalCorporateActionsQuery, {
    variables: {
      tickerSymbols: [tickerSymbol],
      securityCountry: ISecurityCountry.Br,
    },
  });

  const {
    data: currentIssuerDividendsData,
    previousData: previousIssuerDividendsData,
    loading: issuerDividendsLoading,
  } = useQuery<IBrazilianIssuerDividendsQuery, IBrazilianIssuerDividendsQueryVariables>(BrazilianIssuerDividendsQuery, {
    variables: {
      tickerSymbol,
      securityCountry: ISecurityCountry.Br,
      filterByExcludingFrom: filterDividendsByYear,
      filterByExcludingTo: filterDividendsByYear,
    },
  });

  const issuerDividendsData =
    currentIssuerDividendsData && currentIssuerDividendsData ? currentIssuerDividendsData : previousIssuerDividendsData;

  const hasDividends = issuerDividendsData && issuerDividendsData.dividendsPaid.nodes.length > 0;
  const hasStockDividends = issuerDividendsData && issuerDividendsData.stockDividendsPaid.length > 0;
  const hasStockSplit = globalCorporateActionsData && globalCorporateActionsData.stockSplit.length > 0;
  const hasRightsIssues = globalCorporateActionsData && globalCorporateActionsData.rightsIssues.length > 0;

  return (
    <CompanyContainer
      className={classNames("xl:h-fit", {
        "xl:h-full xl:min-h-company-box": enabledFullHeight,
      })}
    >
      <CompanyContentContainer className="grow">
        <Tabs
          defaultValue={CompanyEventsTabEnum.DIVIDENDS}
          value={viewer?.isPremium ? tab : CompanyEventsTabEnum.DIVIDENDS}
          onValueChange={(value) => onChangeTab(value as CompanyEventsTabEnum)}
          className="h-auto w-full"
        >
          <CompanyTabsList
            activeTab={tab}
            tabs={[
              viewer?.isPremium
                ? {
                    value: CompanyEventsTabEnum.CALENDAR,
                    label: intl.formatMessage({
                      defaultMessage: "Calendário",
                      description: "Aba do menu superior da empresa",
                      id: "GiXunu",
                    }),
                    icon: <CalendarDaysIcon className="inline-flex" size={16} strokeWidth={2} />,
                  }
                : null,
              viewer?.isPremium
                ? {
                    value: CompanyEventsTabEnum.RELEASES,
                    label: intl.formatMessage({
                      defaultMessage: "Comunicados",
                      description: "Aba do menu superior da empresa",
                      id: "cZ66Fi",
                    }),
                    icon: <MegaphoneIcon className="inline-flex" size={16} strokeWidth={2} />,
                  }
                : null,
              {
                value: CompanyEventsTabEnum.DIVIDENDS,
                label: intl.formatMessage({
                  defaultMessage: "Dividendos",
                  description: "Aba do menu superior da empresa",
                  id: "cesSrh",
                }),
                icon: <HandCoinsIcon className="inline-flex" size={16} strokeWidth={2} />,
                disabled: !hasDividends,
              },
              viewer?.isPremium
                ? {
                    value: CompanyEventsTabEnum.STOCK_DIVIDENDS,
                    label: intl.formatMessage({
                      defaultMessage: "Bonificação",
                      description: "Aba do menu superior da empresa",
                      id: "XVUdfX",
                    }),
                    icon: <CirclePercentIcon className="inline-flex" size={16} strokeWidth={2} />,
                    disabled: !hasStockDividends,
                  }
                : null,
              viewer?.isPremium
                ? {
                    value: CompanyEventsTabEnum.RIGHTS_ISSUES,
                    label: intl.formatMessage({
                      defaultMessage: "Subscrição",
                      description: "Aba do menu superior da empresa",
                      id: "+HliZT",
                    }),
                    icon: <TicketPercentIcon className="inline-flex" size={16} strokeWidth={2} />,
                    disabled: !hasRightsIssues,
                  }
                : null,
              viewer?.isPremium
                ? {
                    value: CompanyEventsTabEnum.STOCK_SPLIT,
                    label: intl.formatMessage({
                      defaultMessage: "Desdobramento",
                      description: "Aba do menu superior da empresa",
                      id: "VZYL3m",
                    }),
                    icon: <SquareSplitHorizontalIcon className="inline-flex" size={16} strokeWidth={2} />,
                    disabled: !hasStockSplit,
                  }
                : null,
            ].filter(notUndefinedOrNull)}
          />

          {viewer?.isPremium && (
            <CompanyTabsContent value={CompanyEventsTabEnum.RELEASES}>
              <BrazilianIpesContent isRenderPageDefault={isRenderPageDefault} />
            </CompanyTabsContent>
          )}

          {viewer?.isPremium && (
            <CompanyTabsContent value={CompanyEventsTabEnum.CALENDAR} className="py-4">
              <div className="w-full">
                <BrazilianCalendarContent
                  tickerSymbol={tickerSymbol}
                  isRenderPageDefault={isRenderPageDefault}
                  {...filterState}
                />
              </div>
            </CompanyTabsContent>
          )}

          <CompanyTabsContent value={CompanyEventsTabEnum.DIVIDENDS}>
            {issuerDividendsData && issuerDividendsData.dividendsPaid && (
              <BrazilianDividendsContent
                tickerSymbol={tickerSymbol}
                loading={issuerDividendsLoading}
                filterDividendsByYear={filterDividendsByYear}
                setFilterDividendsByYear={setFilterDividendsByYear}
                availableYears={issuerDividendsData.dividendsPaid.availableYears}
                data={issuerDividendsData.dividendsPaid.nodes.filter((node) => node.tickerSymbol === tickerSymbol)}
                dividendYieldSnapshotEachSymbol={issuerDividendsData.dividendsPaid.dividendYieldByTickerSymbol.filter(
                  (node) => node.tickerSymbol === tickerSymbol,
                )}
                isRenderPageDefault={isRenderPageDefault}
                setEnabledFullHeight={setEnabledFullHeight}
              />
            )}
          </CompanyTabsContent>

          {viewer?.isPremium && (
            <CompanyTabsContent value={CompanyEventsTabEnum.STOCK_DIVIDENDS}>
              <BrazilianStockDividendsContent
                data={(issuerDividendsData && issuerDividendsData.stockDividendsPaid) || []}
                isRenderPageDefault={isRenderPageDefault}
              />
            </CompanyTabsContent>
          )}

          {viewer?.isPremium && (
            <CompanyTabsContent value={CompanyEventsTabEnum.RIGHTS_ISSUES}>
              {globalCorporateActionsLoading ? (
                <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />
              ) : (
                <BrazilianRightsIssuesContent
                  data={globalCorporateActionsData?.rightsIssues || []}
                  isRenderPageDefault={isRenderPageDefault}
                />
              )}
            </CompanyTabsContent>
          )}

          {viewer?.isPremium && (
            <CompanyTabsContent value={CompanyEventsTabEnum.STOCK_SPLIT}>
              {globalCorporateActionsLoading ? (
                <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />
              ) : (
                <BrazilianStockSplitContent
                  data={globalCorporateActionsData?.stockSplit || []}
                  isRenderPageDefault={isRenderPageDefault}
                />
              )}
            </CompanyTabsContent>
          )}
        </Tabs>
      </CompanyContentContainer>
    </CompanyContainer>
  );
}
